import { useAppSelector } from '@/app/redux/hooks';
import {
  selectIsAuthenticated,
  selectUserId,
} from '@/app/redux/slices/sessionSlice';
import AdminForgotPassword from '@/auth/AdminForgotPassword';
import AdminResetPassword from '@/auth/AdminResetPassword';
import InviteHandler from '@/auth/InviteHandler';
import VerifyInstructions from '@/auth/VerifyEmail';
import { FeatureSwitch } from '@/common/ui/FeatureGate';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import AssessmentsView from '@/dashboard/Assessment/AssessmentsView';
import AssessmentView, {
  AssessmentsDisabledRedirect,
} from '@/dashboard/Assessment/AssessmentView';
import BulkAdd from '@/dashboard/BulkAdd';
import ChangelogView from '@/dashboard/Changelog/ChangelogView';
import CustomLevelEditorView, {
  CustomLevelsDisabledRedirect,
} from '@/dashboard/CustomLevel/CustomLevelEditorView';
import CustomLevelsView from '@/dashboard/CustomLevel/CustomLevelsView';
import FeatureRequestsView from '@/dashboard/FeatureRequests/FeatureRequestsView';
import GroupsTable from '@/dashboard/Groups/GroupsTable';
import GroupView from '@/dashboard/Groups/GroupView';
import InsightsView from '@/dashboard/Insights/InsightsView';
import LearnerNoteView from '@/dashboard/LearnerNotes/LearnerNoteView';
import IndividualLearnerView from '@/dashboard/LearnerView/IndividualLearnerView';
import ShareLinkView from '@/dashboard/LearnerView/ShareLinkView';
import LessonPlansView from '@/dashboard/LessonPlan/LessonPlansView';
import LessonPlanView from '@/dashboard/LessonPlan/LessonPlanView';
import Onboarding from '@/dashboard/Onboarding';
import MyOrganisations from '@/dashboard/SettingsView/MyOrganisations';
import OrganisationDetails from '@/dashboard/SettingsView/OrganisationDetails';
import OrganisationMembers from '@/dashboard/SettingsView/OrganisationMembers/OrganisationMembers';
import OrgWideLearnerSettings from '@/dashboard/SettingsView/OrgWideLearnerSettings';
import CheckoutConfirmation from '@/dashboard/Upgrade/CheckoutConfirmation';
import CheckoutLayout from '@/dashboard/Upgrade/CheckoutLayout';
import PaymentDetails from '@/dashboard/Upgrade/PaymentDetails';
import PricingTableView from '@/dashboard/Upgrade/PricingTableView';
import WordMat from '@/dashboard/WordMat/WordMat';
import WordMatsView from '@/dashboard/WordMat/WordMatsView';
import WordMatView from '@/dashboard/WordMat/WordMatView';
import GameplayComponent from '@/game/GameComponents/PhaserGame/GameplayComponent';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import AdminLogin from '../auth/AdminLogin';
import AdminSignup from '../auth/AdminSignUp';
import { AuthenticationActionHandler } from '../auth/AuthenticationActionHandler';
import BookPage from '../dashboard/Decodables/BookPage';
import DecodableLibrary from '../dashboard/Decodables/DecodableLibrary';
import LearnersView from '../dashboard/LearnersView';
import ScopeAndSequenceView from '../dashboard/ScopeAndSequenceView';
import Account from '../dashboard/SettingsView/AccountPage';
import BillingView from '../dashboard/SettingsView/Billing/BillingView';
import CheckoutReturn from '../dashboard/Upgrade/CheckoutReturn';
import DashboardLayout from '../views/DashboardLayout';
import ErrorView from '../views/ErrorView';
import { AdminAuthGuard } from './AuthGuard';

function MaybeRedirectToMarketingPage() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isDeployPreview = window.location.hostname.includes('deploy-preview');

  if (isAuthenticated) {
    return <Navigate to={getDefaultDashboardRoute()} replace />;
  } else if (isDeployPreview) {
    return <Navigate to="/login" replace />;
  } else {
    window.location.assign(import.meta.env.VITE_MARKETING_PAGE_URL);
    return <></>;
  }
}

function PageViewTracker() {
  const location = useLocation();
  const userId = useAppSelector(selectUserId);

  useEffect(() => {
    trackMixpanelEvent(`Viewed Page - ${location.pathname}`, {
      path: location.pathname,
    });
  }, [userId, location.pathname]);

  return <Outlet />;
}

// wrap create browser router for sentry performance monitoring
const sentryCreateBrowserRouter =
  Sentry.wrapCreateMemoryRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '',
    errorElement: <ErrorView />,
    element: (
      <Sentry.ErrorBoundary
        fallback={({ error }) => <ErrorView error={error} />}
      >
        <PageViewTracker />
      </Sentry.ErrorBoundary>
    ),
    children: [
      {
        path: '/',
        element: <MaybeRedirectToMarketingPage />,
      },
      {
        path: 'signup',
        element: (
          <AdminAuthGuard>
            <AdminSignup />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'login',
        element: (
          <AdminAuthGuard>
            <AdminLogin />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'verify',
        element: (
          <AdminAuthGuard>
            <VerifyInstructions />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'action',
        element: <AuthenticationActionHandler />,
      },
      {
        path: 'forgot-password',
        element: (
          <AdminAuthGuard>
            <AdminForgotPassword />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <AdminAuthGuard>
            <AdminResetPassword />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'onboarding',
        element: (
          <AdminAuthGuard>
            <Onboarding />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'invite',
        element: (
          <AdminAuthGuard>
            <InviteHandler />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'invite/:token',
        element: (
          <AdminAuthGuard>
            <InviteHandler />
          </AdminAuthGuard>
        ),
      },
      {
        path: 'wordmat',
        element: <WordMat standalone />,
      },
      {
        path: 'lessonplan',
        element: <LessonPlanView standalone />,
      },
      {
        path: 'scopeandsequence',
        element: <ScopeAndSequenceView standalone />,
      },
      {
        path: 'changelog',
        element: <ChangelogView />,
      },
      {
        path: 'pricing',
        element: <PricingTableView />,
      },
      {
        path: 'checkout/return',
        element: <CheckoutReturn />,
      },
      {
        path: 'checkout',
        element: (
          <AdminAuthGuard>
            <CheckoutLayout />
          </AdminAuthGuard>
        ),
        children: [
          {
            path: '',
            element: <Navigate to="payment-details" />,
          },
          {
            path: 'payment-details',
            element: <PaymentDetails />,
          },
          {
            path: 'confirmation',
            element: <CheckoutConfirmation />,
          },
        ],
      },
      {
        path: '/share/:type/:shareLinkId/:tab?',
        element: <ShareLinkView />,
      },
      {
        path: 'dashboard',
        element: (
          <AdminAuthGuard>
            <DashboardLayout />
          </AdminAuthGuard>
        ),
        errorElement: <ErrorView />,
        children: [
          { path: '', element: <Navigate to={getDefaultDashboardRoute()} /> },
          {
            path: 'settings',
            errorElement: <ErrorView />,
            children: [
              {
                path: '',
                element: <Navigate to="/dashboard/settings/profile" />,
              },
              { path: 'profile', element: <Account /> },
              { path: 'my-organisations', element: <MyOrganisations /> },
              { path: 'organisation', element: <OrganisationDetails /> },
              { path: 'members', element: <OrganisationMembers /> },
              { path: 'billing', element: <BillingView /> },
              { path: 'learner-settings', element: <OrgWideLearnerSettings /> },
            ],
          },
          {
            path: 'decodables',
            children: [
              { path: '', element: <DecodableLibrary /> },
              { path: 'book/:title', element: <BookPage /> },
            ],
          },
          {
            path: 'lessonplans',
            element: <LessonPlansView />,
          },
          {
            path: 'lessonplans/:id',
            element: <LessonPlanView />,
          },
          { path: 'bulkadd', element: <BulkAdd /> },
          { path: 'scopeandsequence', element: <ScopeAndSequenceView /> },
          {
            path: 'learners',
            element: <LearnersView />,
          },
          {
            path: 'learners/:studentId/:tab?',
            element: <IndividualLearnerView />,
          },
          {
            path: 'groups',
            element: <GroupsTable />,
          },
          { path: 'groups/:groupId/:tab?', element: <GroupView /> },
          {
            path: 'groups/:groupId/learners/:studentId/:tab?',
            element: <IndividualLearnerView />,
          },
          {
            path: 'assessments',
            element: <AssessmentsView />,
          },
          {
            path: 'assessments/:assessmentId',
            element: (
              <FeatureSwitch
                feature="assessments"
                enabled={<AssessmentView />}
                disabled={<AssessmentsDisabledRedirect />}
              />
            ),
          },
          {
            path: 'insights',
            element: <InsightsView />,
          },
          {
            path: 'learners/:studentId/notes/:noteId',
            element: <LearnerNoteView />,
          },
          {
            path: 'changelog',
            element: <ChangelogView />,
          },
          {
            path: 'wordmats',
            element: <WordMatsView />,
          },
          {
            path: 'wordmats/:id',
            element: <WordMatView />,
          },
          {
            path: 'feature-requests',
            element: <FeatureRequestsView />,
          },
          {
            path: 'customlevels',
            element: <CustomLevelsView />,
          },
          {
            path: 'customlevels/:id',
            element: (
              <FeatureSwitch
                feature="custom-levels"
                enabled={<CustomLevelEditorView />}
                disabled={<CustomLevelsDisabledRedirect />}
              />
            ),
          },
        ],
      },
      {
        path: 'game',
        element: <GameplayComponent />,
        errorElement: <ErrorView />,
      },

      /* Legacy routes */
      {
        path: 'dashboard/placement-tests',
        element: <Navigate to="/dashboard/assessments" replace />,
      },
      {
        path: 'dashboard/stimulus',
        element: <Navigate to="/dashboard/worksheet" replace />,
      },
      {
        path: 'studentauth',
        element: <Navigate to="/game" replace />,
      },
      { path: 'game/*', element: <Navigate to="/game" replace /> },
    ],
  },

  { path: 'error', element: <ErrorView /> },
]);

export default router;
